import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GraduationCap, FileDown, Calculator, Loader2, Info } from "lucide-react";
import { TRIP_TYPES } from "@/data/tripTypes";
import { Sidebar } from "@/components/Sidebar";
import { ResultsSummary } from "@/components/ResultsSummary";
import { LocationInput } from "@/components/LocationInput";
import { DateTimePicker } from "@/components/DateTimePicker";
import { getConfig, createPlan, downloadPdf } from "@/lib/api";

const Field = ({ label, hint, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="text-sm font-medium text-slate-700">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

const inputCls =
  "h-12 bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600/20 focus-visible:border-blue-600";

function App() {
  const [config, setConfig] = useState(null);
  const [activeType, setActiveType] = useState(TRIP_TYPES[0]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [originCoords, setOriginCoords] = useState(null);
  const [destinationCoords, setDestinationCoords] = useState(null);

  const [form, setForm] = useState({
    school_name: "",
    teacher_name: "",
    purpose: "",
    origin: "Zagreb",
    destination: "Plitvička jezera",
    stops: "",
    departure: "",
    return_time: "",
    round_trip: true,
    transport_mode: "osobni_auto",
    num_vehicles: 1,
    transport_cost_per_person: 0,
    num_teachers: 1,
    num_students: 25,
    tolls: 0,
    other_costs: 0,
    override_distance_km: "",
  });

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = () => ({
    ...form,
    stops: form.stops
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    num_vehicles: Number(form.num_vehicles) || 1,
    transport_cost_per_person: Number(form.transport_cost_per_person) || 0,
    num_teachers: Number(form.num_teachers) || 0,
    num_students: Number(form.num_students) || 0,
    tolls: Number(form.tolls) || 0,
    other_costs: Number(form.other_costs) || 0,
    override_distance_km:
      form.override_distance_km === "" ? null : Number(form.override_distance_km),
    origin_coords: originCoords,
    destination_coords: destinationCoords,
  });

  const validate = () => {
    if (!form.departure || !form.return_time) {
      toast.error("Unesite datum i vrijeme polaska i povratka.");
      return false;
    }
    if (form.override_distance_km === "" && (!form.origin || !form.destination)) {
      toast.error("Unesite polazište i odredište (ili ručno unesite udaljenost).");
      return false;
    }
    return true;
  };

  const handleCalculate = async () => {
    if (!validate()) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await createPlan(buildPayload());
      setResult(data);
      toast.success("Izračun je spreman.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Greška pri izračunu. Pokušajte ručno unijeti udaljenost.");
    } finally {
      setLoading(false);
    }
  };

  const handlePdf = async () => {
    if (!validate()) return;
    setPdfLoading(true);
    try {
      await downloadPdf(buildPayload());
      toast.success("PDF putni nalog preuzet.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Greška pri izradi PDF-a.");
    } finally {
      setPdfLoading(false);
    }
  };

  const isCar = form.transport_mode === "osobni_auto";

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-600 text-white">
            <GraduationCap size={22} />
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight leading-none">
              Dnevnice i rute
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Kalkulator za izvanučioničku nastavu — Republika Hrvatska
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <Sidebar
            tripTypes={TRIP_TYPES}
            activeId={activeType.id}
            onSelect={(t) => {
              setActiveType(t);
              setForm((f) => ({ ...f, ...t.defaults }));
            }}
          />

          {/* Content panel */}
          <section className="flex-1 min-w-0">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
                2. Ispunite podatke
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
                {activeType.title}
              </h2>
              <p className="text-slate-500 mt-1">{activeType.description}</p>

              {config && (
                <div className="mt-5 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900">
                  <Info size={16} className="mt-0.5 shrink-0" />
                  <span data-testid="config-banner">
                    Puna dnevnica {config.full_per_diem.toFixed(2)}
                    {config.currency_symbol} · pola {config.half_per_diem.toFixed(2)}
                    {config.currency_symbol} · kilometraža {config.mileage_rate.toFixed(2)}
                    {config.currency_symbol}/km
                  </span>
                </div>
              )}

              {/* Podaci o školi */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Škola" testid="field-school">
                  <Input
                    data-testid="input-school"
                    className={inputCls}
                    value={form.school_name}
                    onChange={(e) => upd("school_name", e.target.value)}
                    placeholder="npr. OŠ Ivana Gundulića"
                  />
                </Field>
                <Field label="Nastavnik/ica" testid="field-teacher">
                  <Input
                    data-testid="input-teacher"
                    className={inputCls}
                    value={form.teacher_name}
                    onChange={(e) => upd("teacher_name", e.target.value)}
                    placeholder="Ime i prezime"
                  />
                </Field>
              </div>

              <div className="mt-6">
                <Field label="Svrha putovanja" testid="field-purpose">
                  <Textarea
                    data-testid="input-purpose"
                    className="bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600/20 focus-visible:border-blue-600"
                    value={form.purpose}
                    onChange={(e) => upd("purpose", e.target.value)}
                    placeholder="npr. Posjet nacionalnom parku u sklopu nastave prirode i društva"
                  />
                </Field>
              </div>

              {/* Ruta */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Polazište" testid="field-origin">
                  <LocationInput
                    testid="input-origin"
                    value={form.origin}
                    onChange={(v) => upd("origin", v)}
                    onCoords={setOriginCoords}
                    placeholder="Grad ili adresa polaska"
                  />
                </Field>
                <Field label="Odredište" testid="field-destination">
                  <LocationInput
                    testid="input-destination"
                    value={form.destination}
                    onChange={(v) => upd("destination", v)}
                    onCoords={setDestinationCoords}
                    placeholder="Grad ili adresa odredišta"
                  />
                </Field>
              </div>

              <div className="mt-6">
                <Field
                  label="Međustanice (nije obavezno)"
                  hint="Odvojite zarezom, npr. Karlovac, Slunj"
                  testid="field-stops"
                >
                  <Input
                    data-testid="input-stops"
                    className={inputCls}
                    value={form.stops}
                    onChange={(e) => upd("stops", e.target.value)}
                    placeholder="Karlovac, Slunj"
                  />
                </Field>
              </div>

              {/* Vrijeme */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Datum i vrijeme polaska" testid="field-departure">
                  <DateTimePicker
                    testid="input-departure"
                    value={form.departure}
                    onChange={(v) => upd("departure", v)}
                  />
                </Field>
                <Field label="Datum i vrijeme povratka" testid="field-return">
                  <DateTimePicker
                    testid="input-return"
                    value={form.return_time}
                    onChange={(v) => upd("return_time", v)}
                  />
                </Field>
              </div>

              {/* Prijevoz */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Vrsta prijevoza" testid="field-transport">
                  <Select
                    value={form.transport_mode}
                    onValueChange={(v) => upd("transport_mode", v)}
                  >
                    <SelectTrigger className={inputCls} data-testid="select-transport">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {config &&
                        Object.entries(config.transport_modes).map(([k, v]) => (
                          <SelectItem key={k} value={k} data-testid={`transport-opt-${k}`}>
                            {v}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </Field>

                {isCar ? (
                  <Field label="Broj vozila" testid="field-vehicles">
                    <Input
                      data-testid="input-vehicles"
                      type="number"
                      min="1"
                      className={inputCls}
                      value={form.num_vehicles}
                      onChange={(e) => upd("num_vehicles", e.target.value)}
                    />
                  </Field>
                ) : (
                  <Field label="Cijena karte po osobi (€)" testid="field-ticket">
                    <Input
                      data-testid="input-ticket"
                      type="number"
                      min="0"
                      step="0.01"
                      className={inputCls}
                      value={form.transport_cost_per_person}
                      onChange={(e) => upd("transport_cost_per_person", e.target.value)}
                    />
                  </Field>
                )}
              </div>

              {/* Sudionici */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Broj pratitelja (nastavnika)"
                  hint="Dnevnice se obračunavaju po pratitelju"
                  testid="field-teachers"
                >
                  <Input
                    data-testid="input-teachers"
                    type="number"
                    min="0"
                    className={inputCls}
                    value={form.num_teachers}
                    onChange={(e) => upd("num_teachers", e.target.value)}
                  />
                </Field>
                <Field label="Broj učenika" testid="field-students">
                  <Input
                    data-testid="input-students"
                    type="number"
                    min="0"
                    className={inputCls}
                    value={form.num_students}
                    onChange={(e) => upd("num_students", e.target.value)}
                  />
                </Field>
              </div>

              {/* Dodatni troškovi */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                <Field label="Cestarine (€)" testid="field-tolls">
                  <Input
                    data-testid="input-tolls"
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputCls}
                    value={form.tolls}
                    onChange={(e) => upd("tolls", e.target.value)}
                  />
                </Field>
                <Field label="Ostali troškovi (€)" testid="field-other">
                  <Input
                    data-testid="input-other"
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputCls}
                    value={form.other_costs}
                    onChange={(e) => upd("other_costs", e.target.value)}
                  />
                </Field>
                <Field
                  label="Ručna udaljenost (km)"
                  hint="Popunite samo ako karta ne radi"
                  testid="field-override"
                >
                  <Input
                    data-testid="input-override-distance"
                    type="number"
                    min="0"
                    step="0.1"
                    className={inputCls}
                    value={form.override_distance_km}
                    onChange={(e) => upd("override_distance_km", e.target.value)}
                    placeholder="npr. 130"
                  />
                </Field>
              </div>

              {/* Povratna ruta */}
              <div className="mt-6 flex items-center justify-between rounded-lg border border-slate-200 p-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Povratna ruta</Label>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Udaljenost se množi s 2 (odlazak i povratak)
                  </p>
                </div>
                <Switch
                  data-testid="switch-round-trip"
                  checked={form.round_trip}
                  onCheckedChange={(v) => upd("round_trip", v)}
                />
              </div>

              {/* Akcije */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="calculate-button"
                  onClick={handleCalculate}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-4 h-auto text-lg font-medium shadow-sm hover:-translate-y-0.5 transition-transform active:scale-95"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Calculator size={20} />
                  )}
                  Izračunaj i prikaži
                </Button>
                <Button
                  data-testid="pdf-button"
                  onClick={handlePdf}
                  disabled={pdfLoading}
                  variant="outline"
                  className="rounded-xl py-4 h-auto text-lg font-medium border-slate-300 hover:bg-slate-100 active:scale-95"
                >
                  {pdfLoading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <FileDown size={20} />
                  )}
                  Preuzmi PDF
                </Button>
              </div>
            </div>

            {result && <ResultsSummary result={result} />}
          </section>
        </div>
      </main>
    </div>
  );
}

export default App;
